import Sentry from '@/services/sentry'
import Vue from 'vue'
import configs from '@/config'
import store from '@/vuex/store'
import { SimpleCache } from '@/utils/cache.utils'
import { mapEssentialPlanNamesToPro } from '@/utils/plan.utils'
import { isValidSlideLimits } from '@/utils/slideLimitRuleset'

let resource = null

export const actions = {
  getUserPlanBenefit: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/plan`,
  },
  // AHA-42358 — entitlements now live on the general-api monolith (the
  // short-lived aha-pricing BFF at /pricing-api/plan-benefit was removed). Same
  // origin + auth as the legacy endpoint above, so it rides the existing Bearer
  // interceptor.
  getUserEntitlements: {
    method: 'GET',
    url: `${configs.baseUrl}/api/pricing/entitlements`,
  },
  getUserLimitationPresentation: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/presentation/{presentationId}/enforce-slidetype`,
  },
  getDataPresentationHandleCopyPlanBenefit: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/presentation/{presentationId}/get-enforce-feature`,
  },
  getMonthlyParticipantPresentCheck: {
    method: 'POST',
    url: `${configs.baseUrl}/api/pricing/monthly-participant/present-check`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

// The new endpoint is a different service returning the same contract; a 2xx
// with a malformed/partial body would otherwise be written straight into the
// plan-benefit store and silently corrupt entitlements. Require the load-bearing
// `name` field before trusting the response.
const isValidEntitlements = (body) => Boolean(body) && typeof body.name === 'string'

// AHA-42054 — the single chokepoint where the essential plan is scrubbed from
// the free-user upgrade funnel. Remapping the `*_planName` hints here (rather
// than at each CTA) means every downstream consumer — store, getters, popovers —
// sees a response that already points at "pro". Gated on the response's own
// `name === 'free'` so it never depends on store/cookie load order, plus
// `canRemoveEssentialPlan` (derived from the shared pricing lineup — see
// pricingLineup module). A no-op for paid users and when Essential is still shown.
const applyNoEssentialOverride = (body) => {
  const enabled = store.getters['featureToggles/canRemoveEssentialPlan'] && body?.name === 'free'
  return mapEssentialPlanNamesToPro(body, enabled)
}

const getUserPlanBenefit = async (params) => {
  try {
    const res = await resource.getUserEntitlements(params)
    if (!isValidEntitlements(res.body)) {
      throw new Error('entitlements response missing required shape')
    }
    return applyNoEssentialOverride(res.body)
  } catch (err) {
    // Fall through to the legacy endpoint on any error or bad payload. Report
    // it so the rollout signal isn't lost — a silent fallback would hide a
    // misbehaving new endpoint. A rejected vue-resource Response is reported
    // unwrapped so beforeSend can drop status 0/4xx and group 5xx by endpoint.
    Sentry.captureException(err || new Error('getUserEntitlements failed'), {
      extra: { userId: store.state.currentUser?.id, status: err?.status },
    })
    const fallback = await resource.getUserPlanBenefit()
    return applyNoEssentialOverride(fallback.body)
  }
}

const getUserLimitationPresentation = async (presentationId, params) => {
  const payload = { ...params, presentationId }
  const res = await resource.getUserLimitationPresentation(payload)
  return res.body
}

/**
 * Fetch the slideLimits ruleset the server serves for one presentation.
 *
 * Deliberately bypasses getUserPlanBenefit: the ?key= projection returns a bare
 * ruleset with no `name`, which isValidEntitlements would reject and route to the
 * legacy endpoint. Resolves null on any failure so callers keep the account-wide
 * ruleset.
 *
 * @param {number|string} presentationId
 * @returns {Promise<object|null>}
 */
const getServedSlideLimits = async (presentationId) => {
  if (!presentationId) return null
  try {
    const res = await resource.getUserEntitlements({ presentationId, key: 'slideLimits' })
    return isValidSlideLimits(res.body) ? res.body : null
  } catch (err) {
    Sentry.captureException(err || new Error('getServedSlideLimits failed'), {
      extra: { presentationId, status: err?.status },
    })
    return null
  }
}

const getDataPresentationHandleCopyPlanBenefit = async (presentationId, params) => {
  const payload = { ...params, presentationId }
  const res = await resource.getDataPresentationHandleCopyPlanBenefit(payload)
  return res.body
}

/**
 * Asks the server for the monthly-participant present nudge stage and stamps
 * the 24h grace clock as a side effect — so it must be called on the Present
 * click, never on mount. Fails open ({ stage: 'none' }) on any error so a
 * pricing-service hiccup never traps the presenter from going live.
 *
 * @returns {Promise<{stage: ('none'|'warn'|'block'), limit?: number, graceEndsAt?: string}>}
 */
const monthlyParticipantPresentCheck = async () => {
  try {
    const res = await resource.getMonthlyParticipantPresentCheck({}, {})
    return res.body
  } catch (err) {
    Sentry.captureException(err instanceof Error ? err : new Error('monthlyParticipantPresentCheck failed'), {
      extra: { userId: store.state.currentUser?.id, status: err?.status },
    })
    return { stage: 'none' }
  }
}

const cache = new SimpleCache()

const getUserPlanBenefitWithCache = async ({ maxAgeInSeconds = 1 }) => {
  return cache.invoke(() => {
    return getUserPlanBenefit()
  }, {
    key: 'getUserPlanBenefit',
    maxAgeInSeconds,
  })
}

export default {
  initResource,
  getUserPlanBenefit,
  getUserPlanBenefitWithCache,
  getUserLimitationPresentation,
  getDataPresentationHandleCopyPlanBenefit,
  getServedSlideLimits,
  monthlyParticipantPresentCheck,
}
